import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { WizardModal, } from '@thoughtspot/radiant-react/widgets/wizard-modal';
import { isWelcomeEmailEnabled } from '@thoughtspot/session-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useAdminUserTracking } from '../../../shared';
import wizardStyles from '../../../shared/wizard-modal.module.scss';
import { useNameAvailability } from '../../../use-name-availability';
import { useCreateUserForm } from '../../hooks/useCreateUserForm';
import styles from './create-user-wizard.module.scss';
import { StepAssignGroups } from './step-assign-groups';
import { StepCredentials } from './step-credentials';
import { StepWelcomeEmail } from './step-welcome-email';
var WizardKeys;
(function (WizardKeys) {
    WizardKeys["TITLE"] = "users.v2.addNewUser";
    WizardKeys["STEP_CREDENTIALS"] = "users.wizard.stepTitle.addCredentials";
    WizardKeys["STEP_ASSIGN_GROUPS"] = "users.wizard.stepTitle.assignGroups";
    WizardKeys["STEP_WELCOME_EMAIL"] = "users.wizard.stepTitle.welcomeEmail";
    WizardKeys["NEXT"] = "modal.next";
    WizardKeys["BACK"] = "roles.wizard.back";
    WizardKeys["ADD"] = "Add";
    WizardKeys["CANCEL"] = "roles.wizard.cancel";
})(WizardKeys || (WizardKeys = {}));
export const CreateUserWizard = ({ isOpen, currentOrgId, isClusterAdmin, onClose, onSuccess, }) => {
    const { t } = useTranslation();
    const tracking = useAdminUserTracking();
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
    const [selectedOrgIds, setSelectedOrgIds] = useState(new Set());
    const [orgError, setOrgError] = useState(null);
    const stepEnteredAtRef = useRef(Date.now());
    const flowStartedAtRef = useRef(Date.now());
    const { form, actions, errors, serverFieldErrors, setServerFieldErrors, isSubmitting, submitError, validateStep1, handleSubmit, resetForm, groupsData, orgsData, } = useCreateUserForm(currentOrgId, onSuccess, selectedGroupIds.size, selectedOrgIds.size);
    const { checkName } = useNameAvailability({
        kind: 'user',
        currentOrgId,
    });
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
    const showWelcomeEmailStep = !isOktaEnabled() && !isClusterAdmin && isWelcomeEmailEnabled();
    const groupItems = useMemo(() => groupsData.list.map(group => ({
        id: group.id,
        label: group.name,
    })), [groupsData.list]);
    const orgItems = useMemo(() => orgsData.list.map(org => ({
        id: org.orgId,
        label: org.orgName,
    })), [orgsData.list]);
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
            setSelectedGroupIds(new Set());
            setSelectedOrgIds(new Set());
            setOrgError(null);
            setIsCheckingAvailability(false);
            resetForm();
            const now = Date.now();
            stepEnteredAtRef.current = now;
            flowStartedAtRef.current = now;
            tracking.createDialogViewed({ dialogVariant: 'single' });
        }
    }, [isOpen, resetForm, tracking]);
    const STEP_NAMES = ['credentials', 'assignments', 'welcome-email'];
    const trackStepCompleted = useCallback((fromStep) => {
        const now = Date.now();
        tracking.createStepCompleted({
            stepName: STEP_NAMES[fromStep] ?? `step-${fromStep}`,
            stepIndex: fromStep,
            timeOnStepMs: now - stepEnteredAtRef.current,
        });
        stepEnteredAtRef.current = now;
    }, [tracking]);
    const handleCancel = useCallback(() => {
        tracking.createCancelled({
            lastStepReached: currentStep,
            timeInFlowMs: Date.now() - flowStartedAtRef.current,
        });
        onClose();
    }, [currentStep, onClose, tracking]);
    useEffect(() => {
        if (selectedOrgIds.size > 0)
            setOrgError(null);
    }, [selectedOrgIds]);
    const handleStep1Next = useCallback(async () => {
        if (!validateStep1())
            return;
        setIsCheckingAvailability(true);
        const result = await checkName(form.name);
        setIsCheckingAvailability(false);
        if (result.status === 'taken') {
            setServerFieldErrors({ name: result.takenMessage });
            return;
        }
        trackStepCompleted(0);
        setCurrentStep(1);
    }, [
        validateStep1,
        checkName,
        form.name,
        setServerFieldErrors,
        trackStepCompleted,
    ]);
    const handleStep2Next = useCallback(() => {
        if (isClusterAdmin && selectedOrgIds.size === 0) {
            setOrgError(t('adminSection.userManagement.errors.selectAtLeastOneOrg'));
            return;
        }
        trackStepCompleted(1);
        setCurrentStep(2);
    }, [isClusterAdmin, selectedOrgIds, t, trackStepCompleted]);
    const handleBackToStep1 = useCallback(() => setCurrentStep(0), []);
    const handleBackToStep2 = useCallback(() => setCurrentStep(1), []);
    const handleFinalSubmit = useCallback(async () => {
        if (!validateStep1()) {
            setCurrentStep(0);
            return;
        }
        trackStepCompleted(currentStep);
        const outcome = await handleSubmit(selectedGroupIds, selectedOrgIds);
        if (!outcome.ok && outcome.rewindToStep !== undefined) {
            setCurrentStep(outcome.rewindToStep);
        }
    }, [
        validateStep1,
        handleSubmit,
        selectedGroupIds,
        selectedOrgIds,
        trackStepCompleted,
        currentStep,
    ]);
    const wrapStep = (content) => (React.createElement("div", { className: styles.stepWithBanner },
        submitError && (React.createElement(Alert.Banner, { isVisible: true, message: submitError, type: BannerAlertType.ERROR, showCloseButton: false })),
        content));
    const wizardSteps = useMemo(() => {
        const steps = [
            {
                stepTitle: t(WizardKeys.STEP_CREDENTIALS),
                stepContent: wrapStep(React.createElement(StepCredentials, { form: form, actions: actions, errors: errors, nameServerError: serverFieldErrors.name })),
                stepForwardButton: {
                    text: t(WizardKeys.NEXT),
                    onNext: handleStep1Next,
                    isDisabled: isSubmitting || isCheckingAvailability,
                    isLoading: isCheckingAvailability,
                },
            },
            {
                stepTitle: isClusterAdmin
                    ? t('users.wizard.stepTitle.assignOrgs')
                    : t(WizardKeys.STEP_ASSIGN_GROUPS),
                stepContent: wrapStep(React.createElement(StepAssignGroups, { groups: groupItems, groupsLoading: groupsData.loading, selectedGroupIds: selectedGroupIds, onGroupSelectionChange: setSelectedGroupIds, groupSearchText: groupsData.searchText, onGroupSearchChange: groupsData.setSearchText, orgs: orgItems, orgsLoading: orgsData.loading, selectedOrgIds: selectedOrgIds, onOrgSelectionChange: setSelectedOrgIds, isClusterAdmin: isClusterAdmin, orgError: orgError })),
                stepForwardButton: showWelcomeEmailStep
                    ? {
                        text: t(WizardKeys.NEXT),
                        onNext: handleStep2Next,
                        isDisabled: isSubmitting,
                    }
                    : {
                        text: t(WizardKeys.ADD),
                        onNext: handleFinalSubmit,
                        isDisabled: isSubmitting,
                        isLoading: isSubmitting,
                    },
                stepBackButton: {
                    text: t(WizardKeys.BACK),
                    onBack: handleBackToStep1,
                },
            },
        ];
        if (showWelcomeEmailStep) {
            steps.push({
                stepTitle: t(WizardKeys.STEP_WELCOME_EMAIL),
                stepContent: wrapStep(React.createElement(StepWelcomeEmail, { sendWelcomeEmail: form.sendWelcomeEmail, onSendWelcomeEmailChange: actions.setSendWelcomeEmail, welcomeEmailMessage: form.welcomeEmailMessage, onWelcomeEmailMessageChange: actions.setWelcomeEmailMessage })),
                stepForwardButton: {
                    text: t(WizardKeys.ADD),
                    onNext: handleFinalSubmit,
                    isDisabled: isSubmitting,
                    isLoading: isSubmitting,
                },
                stepBackButton: {
                    text: t(WizardKeys.BACK),
                    onBack: handleBackToStep2,
                },
            });
        }
        return steps;
    }, [
        t,
        form,
        actions,
        errors,
        serverFieldErrors,
        submitError,
        isSubmitting,
        isCheckingAvailability,
        showWelcomeEmailStep,
        handleStep1Next,
        handleStep2Next,
        handleFinalSubmit,
        handleBackToStep1,
        handleBackToStep2,
        groupItems,
        groupsData,
        orgItems,
        orgsData,
        selectedGroupIds,
        selectedOrgIds,
        orgError,
        isClusterAdmin,
    ]);
    return (React.createElement(WizardModal, { isOpen: isOpen, size: ModalSize.MEDIUM, title: t(WizardKeys.TITLE), onDismiss: handleCancel, denyButton: {
            text: t(WizardKeys.CANCEL),
            onDeny: handleCancel,
        }, wizardModalStep: wizardSteps, currentStep: currentStep, isProgressBarEnabled: true, includesHeader: true, includesFooter: true, className: styles.createUserWizardModal, customHeaderClassName: wizardStyles.wizardHeader, customStepsClassName: wizardStyles.wizardContent, customFooterClassName: wizardStyles.wizardFooter }));
};
//# sourceMappingURL=create-user-wizard.js.map